/**
 * Every fixed page of the site, with its search metadata. This is the one source for:
 * - each page's title, description, canonical URL and robots tag (PageSeo),
 * - the route paths (src/app/App.tsx and the nav),
 * - which pages are prerendered, and the sitemap, robots.txt and llms.txt (src/lib/seoFiles.ts).
 *
 * Articles come from the Markdown blog and nomination pages from src/nominations; both add their
 * own routes on top of these.
 */

type JsonLdPageType = 'WebPage' | 'AboutPage' | 'ContactPage' | 'CollectionPage' | 'Blog' | 'ProfilePage'

export type SitePage = {
  path: string
  /** The page's title, before " | United Accountants" is added. */
  title: string
  description: string
  /** Open to search engines and listed in the sitemap. False adds a noindex robots tag. */
  index: boolean
  sitemap?: { priority: string; changefreq: 'weekly' | 'monthly' | 'yearly' }
  /** Structured data for the page. `name` defaults to the full title; `team` adds the team as people. */
  schema?: { type?: JsonLdPageType; name?: string; team?: boolean }
  /** Lets answer engines quote the whole page. */
  fullSnippet?: boolean
  /** Listed under "Core pages" in llms.txt with this label. */
  llmsName?: string
}

export const sitePages = {
  home: {
    path: '/',
    title: 'Transforming accounting firms',
    description:
      'United invests in accounting firms, combining technology and capital to change how they work and grow. Owners keep leading the firms they built.',
    index: true,
    sitemap: { priority: '1.0', changefreq: 'weekly' },
    schema: { team: true },
  },
  howItWorks: {
    path: '/how-it-works',
    title: 'How we transform accounting firms',
    description:
      'How shared ownership, Justine and acquisition capital work together to transform accounting firms, with owners leading and accountants reviewing the work.',
    index: true,
    sitemap: { priority: '0.9', changefreq: 'monthly' },
    schema: {},
    llmsName: 'How it works',
  },
  about: {
    path: '/about',
    title: 'Our mission and team',
    description:
      'Meet the team behind United and our mission to free accountants from routine work, so they can do more for the small businesses that trust them.',
    index: true,
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    schema: { type: 'AboutPage', team: true },
    llmsName: 'About',
  },
  faq: {
    path: '/faq',
    title: 'Frequently asked questions',
    description:
      'How a partnership with United works for accounting firm owners: the deal, Justine, your team and clients, buying firms, and how to start.',
    index: true,
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    schema: { name: 'Frequently asked questions' },
    fullSnippet: true,
    llmsName: 'FAQ',
  },
  manifesto: {
    path: '/manifesto',
    title: 'Saving humanity from boring work',
    description: 'Saving humanity from boring work. The United manifesto.',
    index: true,
    sitemap: { priority: '0.7', changefreq: 'monthly' },
    schema: { name: 'Saving humanity from boring work' },
    fullSnippet: true,
    llmsName: 'Manifesto',
  },
  /** Closed: vercel.json sends /blog and every article to the home page, with temporary redirects
      so it can reopen. While `index` is false the blog and its articles are noindex and out of the
      sitemap and llms.txt. To reopen: set `index: true`, restore `sitemap`, remove the redirects. */
  blog: {
    path: '/blog',
    title: 'Blog',
    description: 'Automation, operations, and growth notes for tax, bookkeeping, and payroll firms.',
    index: false,
    schema: { type: 'Blog', name: 'United Accountants blog' },
    llmsName: 'Blog',
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy policy',
    description: 'How United Accountants collects, uses and protects personal information, and the choices you have.',
    index: true,
    sitemap: { priority: '0.3', changefreq: 'yearly' },
    schema: { name: 'Privacy policy' },
  },
  // Shared by direct link only: never linked, noindex, not in the sitemap.
  firstTwoMonths: {
    path: '/first-two-months-8k4m2r',
    title: 'Try the partnership',
    description:
      'A partnership test-drive on real clients, with real technology, before either side makes a long-term decision.',
    index: false,
  },
  unitEconomics: {
    path: '/unit-economics-calculator',
    title: 'The unit economics playground',
    description:
      'Compare the unit economics of a regular CPA firm, a firm with offshore staff, and a United firm as clients, margins, and reinvestment change.',
    index: false,
  },
  notFound: {
    path: '/404.html',
    title: 'Page not found',
    description: 'The requested United Accountants page does not exist.',
    index: false,
    schema: { name: 'Page not found' },
  },
} satisfies Record<string, SitePage>

export type SitePageKey = keyof typeof sitePages
